import { apiClient } from './client';
import type {
  PortalDashboardData,
  PortalMyContact,
  PortalCompanySearchResult,
  PortalJoinCodeInfo,
  PortalJoinCode,
} from '../types';

// ─────────────────────────────────────────────────────────────
// Customer-facing portal API
// ─────────────────────────────────────────────────────────────

export const portalApi = {
  searchCompanies: async (q: string): Promise<PortalCompanySearchResult[]> => {
    const { data } = await apiClient.get('/portal/companies/search', { params: { q } });
    return data.data;
  },

  getJoinCodeInfo: async (code: string): Promise<PortalJoinCodeInfo> => {
    const { data } = await apiClient.get(`/portal/join/${code}`);
    return data.data;
  },

  joinCompany: async (code: string): Promise<{ companyId: string; companyName: string; organizationName?: string; alreadyMember?: boolean }> => {
    const { data } = await apiClient.post('/portal/join', { code });
    return data.data;
  },

  getDashboard: async (): Promise<PortalDashboardData> => {
    const { data } = await apiClient.get('/portal/dashboard');
    return data.data;
  },

  getMyContact: async (): Promise<PortalMyContact | null> => {
    const { data } = await apiClient.get('/portal/my-contact');
    return data.data;
  },
};

// ─────────────────────────────────────────────────────────────
// Admin portal management API
// ─────────────────────────────────────────────────────────────

export const portalAdminApi = {
  listJoinCodes: async (): Promise<PortalJoinCode[]> => {
    const { data } = await apiClient.get('/portal-admin/join-codes');
    return data.data;
  },

  createJoinCode: async (payload: {
    label?: string;
    crmOrganizationId?: string;
    maxUses?: number;
    expiresAt?: string;
  }): Promise<PortalJoinCode> => {
    const { data } = await apiClient.post('/portal-admin/join-codes', payload);
    return data.data;
  },

  updateJoinCode: async (id: string, payload: Partial<{
    label: string;
    crmOrganizationId: string | null;
    maxUses: number | null;
    expiresAt: string | null;
    isActive: boolean;
  }>): Promise<PortalJoinCode> => {
    const { data } = await apiClient.put(`/portal-admin/join-codes/${id}`, payload);
    return data.data;
  },

  deleteJoinCode: async (id: string): Promise<void> => {
    await apiClient.delete(`/portal-admin/join-codes/${id}`);
  },

  listCustomers: async (): Promise<any[]> => {
    const { data } = await apiClient.get('/portal-admin/customers');
    return data.data;
  },
};
